import { hcWithType } from "@nimble/candidate-api/hc";
import {
  JobPostingType,
  StateAbbreviation,
} from "@nimble/data-service-client/types";
import type { StateAbbreviation as CredentialStateAbbreviation } from "@nimble/datamodel/enums/states";
import type {
  ApplicationSource,
  ApplicationUpdate,
} from "@nimble/datamodel/schemas/application";
import { CredentialGradeOptionSchema } from "@nimble/datamodel/schemas/credentialGrade";
import { CredentialStatusOptionSchema } from "@nimble/datamodel/schemas/credentialStatus";
import type { ApplicationId } from "@nimble/datamodel/types/applicationId";
import type {
  DistrictNcesId,
  SchoolNcesId,
} from "@nimble/datamodel/types/ncesId";
import type { NimbleAtsJobId } from "@nimble/datamodel/types/nimbleAtsJobId";
import { UserId } from "@nimble/datamodel/types/userId";
import { CachingStrategy } from "@nimble/react-requests/api-clients/caching-strategy";
import { raisableFetch } from "@nimble/react-requests/api-clients/fetch-config";
import { SuccessResponseType } from "@nimble/react-requests/data/response-utils";
import z from "zod";

import { nextServerEnv } from "@/env/next-server-env";
import {
  GATEWAY_PROXY_PATH,
  REVERSE_PROXY_GATEWAY_PATH,
} from "@/lib/constants";

const candidateApiClient = hcWithType(
  // If on server, uses full URL. If on client, uses proxied path
  nextServerEnv.GATEWAY_HOSTPORT
    ? `${nextServerEnv.GATEWAY_PROTOCOL}://${nextServerEnv.GATEWAY_HOSTPORT}${GATEWAY_PROXY_PATH}/candidate-api`
    : `${REVERSE_PROXY_GATEWAY_PATH}/candidate-api`,
  {
    fetch: raisableFetch,
  },
);

export type PostSchoolsSearchJson = Parameters<
  (typeof candidateApiClient.api)["no-auth"]["v1"]["school"]["$post"]
>[0]["json"];

export const postSchoolsSearch =
  (cachingStrategy: CachingStrategy) => async (json: PostSchoolsSearchJson) => {
    const response = await candidateApiClient.api["no-auth"]["v1"][
      "school"
    ].$post(
      {
        json: json,
      },
      cachingStrategy,
    );

    const responseJson = await response.json();

    return responseJson as SuccessResponseType<typeof responseJson>;
  };

export type PostUserSchoolsSearchJson = Parameters<
  (typeof candidateApiClient.api)["user-auth"]["v1"]["user-schools"]["search"]["$post"]
>[0]["json"];
type PostUserSchoolsSearchHeaders = Parameters<
  (typeof candidateApiClient.api)["user-auth"]["v1"]["user-schools"]["search"]["$post"]
>[0]["header"];
export type PostUserSchoolsSearchResponse = SuccessResponseType<
  Awaited<ReturnType<ReturnType<typeof postUserSchoolsSearch>>>
>;

export const postUserSchoolsSearch =
  (cachingStrategy: CachingStrategy) =>
  async (
    header: PostUserSchoolsSearchHeaders,
    json: PostUserSchoolsSearchJson,
  ) => {
    const response = await candidateApiClient.api["user-auth"]["v1"][
      "user-schools"
    ]["search"].$post(
      {
        json: json,
        header: header,
      },
      cachingStrategy,
    );

    const responseJson = await response.json();

    return responseJson as SuccessResponseType<typeof responseJson>;
  };

export const getDistrictAutocomplete =
  (cachingStrategy: CachingStrategy) =>
  async (
    districts?: string[] | null,
    search?: string | null,
    states?: string[] | null,
  ) => {
    const response = await candidateApiClient.api["no-auth"]["v1"]["district"][
      "autocomplete"
    ].$get(
      {
        query: {
          ...(districts && districts.length > 0 && { districts }),
          ...(search && search.trim() && { search }),
          ...(states && states.length > 0 && { states }),
        },
      },
      cachingStrategy,
    );

    const responseJson = await response.json();

    return responseJson as SuccessResponseType<typeof responseJson>;
  };

export const getDistrictData =
  (cachingStrategy: CachingStrategy) => async (ncesId: DistrictNcesId) => {
    const response = await candidateApiClient.api["no-auth"]["v1"]["district"][
      ":nces_id"
    ].$get(
      {
        param: { nces_id: ncesId },
      },
      cachingStrategy,
    );

    const responseJson = await response.json();

    return responseJson as SuccessResponseType<typeof responseJson>;
  };

export const getJobData =
  (cachingStrategy: CachingStrategy) => async (jobId: string) => {
    const response = await candidateApiClient.api["no-auth"]["v1"]["job"][
      ":job_id"
    ].$get(
      {
        param: { job_id: jobId },
      },
      cachingStrategy,
    );

    const responseJson = await response.json();

    return responseJson as SuccessResponseType<typeof responseJson>;
  };

export const getJobsData =
  (cachingStrategy: CachingStrategy) =>
  async (schoolNcesId: string, page: number) => {
    const response = await candidateApiClient.api["no-auth"]["v1"]["job"][
      "search"
    ].$post(
      {
        json: {
          schools: [schoolNcesId],
          page: page,
          postingType: JobPostingType.TEACHING,
        },
      },
      cachingStrategy,
    );

    const responseJson = await response.json();

    return responseJson as SuccessResponseType<typeof responseJson>;
  };

type PostJobsSearchJson = Parameters<
  (typeof candidateApiClient.api)["no-auth"]["v1"]["job"]["search"]["$post"]
>[0]["json"];

export type NonTeachingJobsFilters = Required<
  Pick<
    Extract<
      PostJobsSearchJson,
      { postingType: typeof JobPostingType.NON_TEACHING }
    >,
    "query" | "subcategories" | "employmentTypes"
  >
>;

export const getDistrictNonTeachingJobsData =
  (cachingStrategy: CachingStrategy) =>
  async (
    districtId: DistrictNcesId,
    page: number,
    filters: NonTeachingJobsFilters,
  ) => {
    const response = await candidateApiClient.api["no-auth"]["v1"]["job"][
      "search"
    ].$post(
      {
        json: {
          districts: [districtId],
          postingType: JobPostingType.NON_TEACHING,
          page,
          query: filters.query,
          subcategories: filters.subcategories,
          employmentTypes: filters.employmentTypes,
        },
      },
      cachingStrategy,
    );

    const responseJson = await response.json();

    return responseJson as SuccessResponseType<typeof responseJson>;
  };

export const getSchoolData =
  (cachingStrategy: CachingStrategy) => async (id: string) => {
    const response = await candidateApiClient.api["no-auth"]["v1"]["school"][
      ":nces_id"
    ].$get(
      {
        param: { nces_id: id },
      },
      cachingStrategy,
    );

    const responseJson = await response.json();

    return responseJson as SuccessResponseType<typeof responseJson>;
  };

export const batchSchoolData =
  (cachingStrategy: CachingStrategy) => async (ncesIds: string[]) => {
    const response = await candidateApiClient.api["no-auth"]["v1"]["school"][
      "batch-info"
    ].$post(
      {
        json: {
          nces_ids: ncesIds,
        },
      },
      cachingStrategy,
    );

    const responseJson = await response.json();

    return responseJson as SuccessResponseType<typeof responseJson>;
  };

export type BatchSchoolDataResponse = Awaited<
  ReturnType<ReturnType<typeof batchSchoolData>>
>;

export const getSchoolAutocomplete =
  (cachingStrategy: CachingStrategy) =>
  async (
    schools: string[] | null,
    search: string | null,
    states: StateAbbreviation[],
    districts: DistrictNcesId[],
  ) => {
    const response = await candidateApiClient.api["no-auth"]["v1"]["school"][
      "autocomplete"
    ].$get(
      {
        query: {
          ...(schools && schools.length > 0 && { schools }),
          ...(search && search.trim() && { search }),
          ...(states.length > 0 && { states }),
          ...(districts.length > 0 && { districts }),
        },
      },
      cachingStrategy,
    );

    const responseJson = await response.json();

    return responseJson as SuccessResponseType<typeof responseJson>;
  };

export const getSchoolFeaturesForRecommendations =
  (cachingStrategy: CachingStrategy) =>
  async ({
    latitude,
    longitude,
    radiusMiles,
  }: {
    latitude: number;
    longitude: number;
    radiusMiles: number;
  }) => {
    const response = await candidateApiClient.api["no-auth"]["v1"]["quiz"][
      "school-features-for-recommendations"
    ].$post(
      {
        json: {
          latitude,
          longitude,
          radiusMiles,
        },
      },
      cachingStrategy,
    );

    const responseJson = await response.json();

    return responseJson as SuccessResponseType<typeof responseJson>;
  };

export const existsSavedSchools = async (
  userId: UserId,
  ncesIds: SchoolNcesId[],
) => {
  const response = await candidateApiClient.api["user-auth"]["v1"][
    "saved-schools"
  ]["exists"].$post({
    json: {
      nces_ids: ncesIds,
    },
    header: {
      "x-user-id": userId.toString(),
    },
  });

  const responseJson = await response.json();

  return responseJson as SuccessResponseType<typeof responseJson>;
};

export const createSavedSchool = async (
  userId: UserId,
  ncesId: SchoolNcesId,
) => {
  const response = await candidateApiClient.api["user-auth"]["v1"][
    "saved-schools"
  ][":nces_id"].$post({
    param: { nces_id: ncesId },
    header: {
      "x-user-id": userId.toString(),
    },
  });

  const responseJson = await response.json();

  return responseJson as SuccessResponseType<typeof responseJson>;
};

export const deleteSavedSchool = async (
  userId: UserId,
  ncesId: SchoolNcesId,
) => {
  const response = await candidateApiClient.api["user-auth"]["v1"][
    "saved-schools"
  ][":nces_id"].$delete({
    param: { nces_id: ncesId },
    header: {
      "x-user-id": userId.toString(),
    },
  });

  const responseJson = await response.json();

  return responseJson as SuccessResponseType<typeof responseJson>;
};

export type CreateSavedSearchJson = Parameters<
  (typeof candidateApiClient.api)["user-auth"]["v1"]["saved-searches"]["$post"]
>[0]["json"];

export const createSavedSearch = async (
  userId: UserId,
  json: CreateSavedSearchJson,
) => {
  const response = await candidateApiClient.api["user-auth"]["v1"][
    "saved-searches"
  ].$post({
    json,
    header: { "x-user-id": userId.toString() },
  });

  const responseJson = await response.json();
  return responseJson as SuccessResponseType<typeof responseJson>;
};

type GetSavedSearchesQuery = Parameters<
  (typeof candidateApiClient.api)["user-auth"]["v1"]["saved-searches"]["$get"]
>[0]["query"];

export type SavedSearchesScope = NonNullable<GetSavedSearchesQuery["scope"]>;

const buildSavedSearchesQuery = (params?: {
  state?: StateAbbreviation;
  scope?: SavedSearchesScope;
}): GetSavedSearchesQuery => {
  if (params?.scope) return { scope: params.scope };
  if (params?.state) return { state: params.state };
  return {};
};

export const getSavedSearches = async (
  userId: UserId,
  params?: { state?: StateAbbreviation; scope?: SavedSearchesScope },
) => {
  const query = buildSavedSearchesQuery(params);

  const response = await candidateApiClient.api["user-auth"]["v1"][
    "saved-searches"
  ].$get({
    query,
    header: { "x-user-id": userId.toString() },
  });

  const responseJson = await response.json();
  return responseJson as SuccessResponseType<typeof responseJson>;
};

export const createApplication = async (
  userId: UserId,
  jobId: NimbleAtsJobId,
) => {
  const response = await candidateApiClient.api["user-auth"]["v1"][
    "applications"
  ].$post({
    header: {
      "x-user-id": userId.toString(),
    },
    json: { jobId: jobId },
  });

  const responseJson = await response.json();

  return responseJson as SuccessResponseType<typeof responseJson>;
};

export type CreateApplicationResponse = Awaited<
  ReturnType<typeof createApplication>
>;

export const getApplications = async (userId: UserId) => {
  const response = await candidateApiClient.api["user-auth"]["v1"][
    "applications"
  ].$get({
    header: {
      "x-user-id": userId.toString(),
    },
  });

  const responseJson = await response.json();

  return responseJson as SuccessResponseType<typeof responseJson>;
};

export type GetApplicationsResponse = Awaited<
  ReturnType<typeof getApplications>
>;
export type CandidateApplication = GetApplicationsResponse["data"][number];

export const getApplicationDetail = async (
  userId: UserId,
  applicationId: string,
) => {
  const response = await candidateApiClient.api["user-auth"]["v1"][
    "applications"
  ][":application_id"].$get({
    param: { application_id: applicationId },
    header: {
      "x-user-id": userId.toString(),
    },
  });

  const responseJson = await response.json();

  return responseJson as SuccessResponseType<typeof responseJson>;
};

export type GetApplicationDetailResponse = Awaited<
  ReturnType<typeof getApplicationDetail>
>;
export type ApplicationDetail = GetApplicationDetailResponse["data"];

export const uploadApplicationFile = async (
  userId: UserId,
  applicationId: string,
  file: File,
) => {
  const response = await candidateApiClient.api["user-auth"]["v1"][
    "applications"
  ][":application_id"]["files"].$post({
    param: { application_id: applicationId },
    header: {
      "x-user-id": userId.toString(),
    },
    form: { file },
  });

  const responseJson = await response.json();

  return responseJson as SuccessResponseType<typeof responseJson>;
};

export type UploadApplicationFileResponse = Awaited<
  ReturnType<typeof uploadApplicationFile>
>;
export type UploadedFile = UploadApplicationFileResponse["data"];

export const updateApplication = async (
  userId: UserId,
  applicationId: string,
  body: ApplicationUpdate,
) => {
  const response = await candidateApiClient.api["user-auth"]["v1"][
    "applications"
  ][":application_id"].$patch({
    param: { application_id: applicationId },
    header: {
      "x-user-id": userId.toString(),
    },
    json: body,
  });

  const responseJson = await response.json();

  return responseJson as SuccessResponseType<typeof responseJson>;
};

export const submitApplication = async (
  userId: UserId,
  applicationId: string,
) => {
  const response = await candidateApiClient.api["user-auth"]["v1"][
    "applications"
  ][":application_id"]["submit"].$post({
    param: { application_id: applicationId },
    header: {
      "x-user-id": userId.toString(),
    },
  });

  const responseJson = await response.json();

  return responseJson as SuccessResponseType<typeof responseJson>;
};

export const withdrawApplication = async (
  userId: UserId,
  applicationId: ApplicationId,
) => {
  const response = await candidateApiClient.api["user-auth"]["v1"][
    "applications"
  ][":application_id"]["withdraw"].$post({
    param: { application_id: applicationId },
    header: {
      "x-user-id": userId.toString(),
    },
  });

  const responseJson = await response.json();

  return responseJson as SuccessResponseType<typeof responseJson>;
};

export const parseResume = async (file: File) => {
  const response = await candidateApiClient.api["user-auth"]["v1"]["resume"][
    "parse-resume"
  ].$post({
    form: { file },
  });

  const responseJson = await response.json();

  return responseJson as SuccessResponseType<typeof responseJson>;
};

export const deleteSavedSearch = async (userId: UserId, uuid: string) => {
  const response = await candidateApiClient.api["user-auth"]["v1"][
    "saved-searches"
  ][":uuid"].$delete({
    param: { uuid },
    header: { "x-user-id": userId.toString() },
  });

  const responseJson = await response.json();
  return responseJson as SuccessResponseType<typeof responseJson>;
};

export type EditJobAlertsJson = Parameters<
  (typeof candidateApiClient.api)["user-auth"]["v1"]["job-alerts"]["$put"]
>[0]["json"];

export const getJobAlerts = async (userId: UserId) => {
  const response = await candidateApiClient.api["user-auth"]["v1"][
    "job-alerts"
  ].$get({ header: { "x-user-id": userId.toString() } });
  const responseJson = await response.json();
  return responseJson as SuccessResponseType<typeof responseJson>;
};

export const editJobAlerts = async (
  userId: UserId,
  jobAlert: EditJobAlertsJson,
) => {
  const response = await candidateApiClient.api["user-auth"]["v1"][
    "job-alerts"
  ].$put({
    json: jobAlert,
    header: {
      "x-user-id": userId.toString(),
    },
  });

  const responseJson = await response.json();

  return responseJson as SuccessResponseType<typeof responseJson>;
};

export const getApplicationSections = async (
  userId: UserId,
  jobId: NimbleAtsJobId,
) => {
  const response = await candidateApiClient.api["user-auth"]["v1"][
    "applications"
  ]["job"][":job_id"]["sections"].$get({
    param: { job_id: jobId },
    header: { "x-user-id": userId.toString() },
  });

  const responseJson = await response.json();

  return responseJson as SuccessResponseType<typeof responseJson>;
};

export type GetApplicationSectionsResponse = Awaited<
  ReturnType<typeof getApplicationSections>
>;

export const getExamOptionTypes = async () => {
  const response =
    await candidateApiClient.api["user-auth"]["v1"]["exam-options"][
      "types"
    ].$get();

  const responseJson = await response.json();

  return responseJson as SuccessResponseType<typeof responseJson>;
};

export const getExamOptionSubjects = async () => {
  const response =
    await candidateApiClient.api["user-auth"]["v1"]["exam-options"][
      "subjects"
    ].$get();

  const responseJson = await response.json();

  return responseJson as SuccessResponseType<typeof responseJson>;
};

const CredentialGradesResponseSchema = z.object({
  data: z.array(CredentialGradeOptionSchema),
});

export const getCredentialGrades = async (query: {
  state: CredentialStateAbbreviation;
  source: ApplicationSource;
}) => {
  const response = await candidateApiClient.api["user-auth"]["v1"][
    "credential-grades"
  ].$get({
    query,
  });

  return CredentialGradesResponseSchema.parse(await response.json()).data;
};

const CredentialStatusesResponseSchema = z.object({
  data: z.array(CredentialStatusOptionSchema),
});

export const getCredentialStatuses = async (query: {
  state: CredentialStateAbbreviation;
  source: ApplicationSource;
}) => {
  const response = await candidateApiClient.api["user-auth"]["v1"][
    "credential-statuses"
  ].$get({
    query,
  });

  return CredentialStatusesResponseSchema.parse(await response.json()).data;
};
